// Web Push (VAPID) subscription helpers.
// Encapsulates the browser dance: fetch server public key → convert to Uint8Array →
// call `pushManager.subscribe()` → POST the returned PushSubscription to /api/push/subscribe.
// Every function is a no-op on unsupported browsers so calling sites don't need to feature-detect.
import { api } from "./api";
import { logger } from "./logger";

// urlBase64ToUint8Array — the VAPID public key is delivered as URL-safe Base64 (no padding).
// The Push API needs a raw Uint8Array. This helper does the conversion in-browser.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export async function getCurrentSubscription() {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch (e) {
    logger.warn("getCurrentSubscription failed:", e?.message);
    return null;
  }
}

// Subscribe the current browser to Web Push. Returns { ok, reason }.
// Callers should surface `reason` in a toast on failure — the common ones are
// "unsupported", "denied" (user blocked), "insecure" (non-HTTPS), and "no_key".
export async function subscribeToPush() {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  if (Notification.permission === "denied") return { ok: false, reason: "denied" };
  if (Notification.permission !== "granted") {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return { ok: false, reason: perm === "denied" ? "denied" : "dismissed" };
  }
  try {
    const { data } = await api.get("/push/public-key");
    const publicKey = data?.public_key;
    if (!publicKey) return { ok: false, reason: "no_key" };

    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
    await api.post("/push/subscribe", {
      endpoint: sub.endpoint,
      keys: {
        p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(sub.getKey("p256dh")))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""),
        auth: btoa(String.fromCharCode.apply(null, new Uint8Array(sub.getKey("auth")))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""),
      },
      user_agent: navigator.userAgent,
    });
    return { ok: true };
  } catch (e) {
    logger.warn("subscribeToPush failed:", e?.message);
    return { ok: false, reason: "error", detail: e?.message };
  }
}

export async function unsubscribeFromPush() {
  const sub = await getCurrentSubscription();
  if (!sub) return { ok: true };
  try {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
    await sub.unsubscribe();
    return { ok: true };
  } catch (e) {
    logger.warn("unsubscribeFromPush failed:", e?.message);
    return { ok: false, reason: "error" };
  }
}
